'use client';

import { useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { Button } from '@/shared/components/Button';
import VideoCarousel from '@/shared/components/VideoCarousel';
import { useAnalytics } from '@/core/analytics';

export default function PrivacyPage() {
	const analytics = useAnalytics();

	useEffect(() => {
		analytics.logEvent('privacy_page_visited');
	}, []);

	return (
		<div className="min-h-screen overflow-hidden">
			{/* Fullscreen Video Background */}
			<div className="fixed inset-0 z-0">
				<VideoCarousel />
			</div>

			{/* Navigation */}
			<nav className="glass-dark fixed top-0 left-0 right-0 z-50 border-b border-white/10">
				<div className="container mx-auto px-4 py-4 flex justify-between items-center">
					<Link href="/" className="flex items-center">
						<Image
							src="/ts-logo2-v2.png"
							alt="TabStudio Logo"
							width={160}
							height={53}
							className="h-12 w-auto"
							loading="lazy"
							unoptimized
						/>
					</Link>
					<div className="flex items-center gap-4">
						<Link href="/login" prefetch={false}>
							<Button
								variant="ghost"
								size="sm"
								className="glass-light glass-hover text-white border-white/20"
							>
								Sign In
							</Button>
						</Link>
						<Link href="/register" prefetch={false}>
							<Button
								variant="default"
								size="sm"
								className="glass-frosted glass-hover bg-white/90 text-black font-semibold border-white/30"
							>
								Get Started
							</Button>
						</Link>
					</div>
				</div>
			</nav>

			<main className="relative z-10 pt-24">
				{/* Hero Section */}
				<section className="py-24 px-4">
					<div className="container mx-auto max-w-4xl">
						<h1 className="text-5xl md:text-7xl font-bold mb-6 text-center">
							<span className="bg-gradient-to-r from-red-500 to-red-700 bg-clip-text text-transparent">
								Privacy
							</span>{' '}
							Policy
						</h1>
						<p className="text-xl text-muted-foreground text-center mb-8">
							Last Updated: 1 November 2025
						</p>
					</div>
				</section>

				{/* Content */}
				<section className="py-16 px-4">
					<div className="container mx-auto max-w-4xl">
						<div className="prose prose-invert max-w-none">
							<div className="space-y-8">
								{/* Introduction */}
								<div>
									<p className="text-muted-foreground leading-relaxed mb-4">
										This Privacy Policy (&quot;Policy&quot;)
										explains how TabSpace AI Pte. Ltd. and
										its affiliates (collectively,
										&quot;TabStudio&quot;, &quot;we&quot;,
										&quot;our&quot;, or &quot;us&quot;)
										collect, use, disclose, store, transfer,
										and protect personal data in connection
										with our websites, applications,
										products, and services, including
										AI-generated video and creative
										production tools (the
										&quot;Services&quot;).
									</p>
									<p className="text-muted-foreground leading-relaxed mb-4">
										This Policy applies globally and is
										designed to comply with leading privacy
										regulations, including the EU General
										Data Protection Regulation (GDPR), UK
										GDPR, California CCPA/CPRA, Singapore
										PDPA, Malaysia PDPA, Brazil LGPD, Canada
										PIPEDA, and other applicable laws. Where
										local laws provide stronger protections,
										we will follow the stricter requirement.
									</p>
									<p className="text-muted-foreground leading-relaxed">
										By accessing or using our Services, you
										acknowledge that you have read and
										understood this Policy.
									</p>
								</div>

								{/* Who We Are */}
								<div>
									<h2 className="text-2xl font-bold mb-4">
										1. WHO WE ARE
									</h2>
									<p className="text-muted-foreground leading-relaxed mb-3">
										For purposes of applicable data
										protection laws:
									</p>
									<div className="overflow-x-auto mb-4">
										<table className="min-w-full text-sm border border-white/10">
											<thead className="bg-white/5">
												<tr>
													<th className="px-4 py-2 text-left border border-white/10">
														Role
													</th>
													<th className="px-4 py-2 text-left border border-white/10">
														Entity
													</th>
													<th className="px-4 py-2 text-left border border-white/10">
														Responsibility
													</th>
												</tr>
											</thead>
											<tbody>
												<tr>
													<td className="px-4 py-2 border border-white/10">
														Data Controller (Global,
														including EU/UK)
													</td>
													<td className="px-4 py-2 border border-white/10">
														TabSpace AI Pte. Ltd.
														(Singapore)
													</td>
													<td className="px-4 py-2 border border-white/10">
														Determines the purposes
														and means of processing
														personal data
													</td>
												</tr>
												<tr>
													<td className="px-4 py-2 border border-white/10">
														Regional Processor /
														Sub-Processor
													</td>
													<td className="px-4 py-2 border border-white/10">
														TabSpace Technologies
														Sdn. Bhd. (Malaysia)
													</td>
													<td className="px-4 py-2 border border-white/10">
														Processes personal data
														on behalf of the
														Controller
													</td>
												</tr>
											</tbody>
										</table>
									</div>
									<p className="text-muted-foreground leading-relaxed">
										<strong>
											Primary Contact (DPO/Privacy
											Office):
										</strong>
										<br />
										Email:{' '}
										<a
											href="mailto:privacy@tabspace.ai"
											className="text-primary hover:underline"
										>
											privacy@tabspace.ai
										</a>
									</p>
								</div>

								{/* Scope */}
								<div>
									<h2 className="text-2xl font-bold mb-4">
										2. SCOPE
									</h2>
									<p className="text-muted-foreground leading-relaxed mb-3">
										This Policy covers personal data we
										process when you:
									</p>
									<ul className="list-disc list-inside text-muted-foreground space-y-2 mb-3">
										<li>
											Use TabStudio applications,
											websites, and platforms
										</li>
										<li>
											Create an account or user profile
										</li>
										<li>Upload content or assets</li>
										<li>Communicate with us</li>
										<li>
											Participate in beta programmes,
											promotions, or training features
										</li>
									</ul>
									<p className="text-muted-foreground leading-relaxed">
										This Policy does not apply to
										third-party platforms that integrate
										with TabStudio. Their privacy policies
										govern their data.
									</p>
								</div>

								{/* Personal Data We Collect */}
								<div>
									<h2 className="text-2xl font-bold mb-4">
										3. PERSONAL DATA WE COLLECT
									</h2>
									<p className="text-muted-foreground leading-relaxed mb-3">
										We may collect the following categories
										of data:
									</p>
									<div className="overflow-x-auto mb-4">
										<table className="min-w-full text-sm border border-white/10">
											<thead className="bg-white/5">
												<tr>
													<th className="px-4 py-2 text-left border border-white/10">
														Category
													</th>
													<th className="px-4 py-2 text-left border border-white/10">
														Examples
													</th>
												</tr>
											</thead>
											<tbody>
												<tr>
													<td className="px-4 py-2 border border-white/10">
														Account Data
													</td>
													<td className="px-4 py-2 border border-white/10">
														Name, email, password,
														profile data,
														organisation
													</td>
												</tr>
												<tr>
													<td className="px-4 py-2 border border-white/10">
														Authentication &
														Security Data
													</td>
													<td className="px-4 py-2 border border-white/10">
														Login metadata, IP,
														device identifiers
													</td>
												</tr>
												<tr>
													<td className="px-4 py-2 border border-white/10">
														Payment & Billing
													</td>
													<td className="px-4 py-2 border border-white/10">
														Billing name, card
														token, invoices
														(processed via
														PCI-compliant processors
														such as Stripe)
													</td>
												</tr>
												<tr>
													<td className="px-4 py-2 border border-white/10">
														User Content (Your
														Assets)
													</td>
													<td className="px-4 py-2 border border-white/10">
														Storyboards, scripts,
														photos, video, audio,
														animation assets
													</td>
												</tr>
												<tr>
													<td className="px-4 py-2 border border-white/10">
														AI Processing Data
													</td>
													<td className="px-4 py-2 border border-white/10">
														Temporary inference data
														generated to fulfil
														requests
													</td>
												</tr>
												<tr>
													<td className="px-4 py-2 border border-white/10">
														Usage & Analytics Data
													</td>
													<td className="px-4 py-2 border border-white/10">
														Interactions, logs,
														crash reports,
														performance metrics
													</td>
												</tr>
												<tr>
													<td className="px-4 py-2 border border-white/10">
														Device/Technical Data
													</td>
													<td className="px-4 py-2 border border-white/10">
														Browser, OS, timezone,
														cookies, network
														metadata
													</td>
												</tr>
											</tbody>
										</table>
									</div>
									<p className="text-muted-foreground leading-relaxed">
										We do not collect special-category data
										unless you intentionally upload it.
									</p>
								</div>

								{/* Purposes and Legal Bases */}
								<div>
									<h2 className="text-2xl font-bold mb-4">
										4. PURPOSES AND LEGAL BASES (GDPR Art.
										6)
									</h2>
									<p className="text-muted-foreground leading-relaxed mb-3">
										We process data for:
									</p>
									<div className="overflow-x-auto mb-4">
										<table className="min-w-full text-sm border border-white/10">
											<thead className="bg-white/5">
												<tr>
													<th className="px-4 py-2 text-left border border-white/10">
														Purpose
													</th>
													<th className="px-4 py-2 text-left border border-white/10">
														Legal Basis
													</th>
												</tr>
											</thead>
											<tbody>
												<tr>
													<td className="px-4 py-2 border border-white/10">
														Provide and operate the
														Services
													</td>
													<td className="px-4 py-2 border border-white/10">
														Contractual necessity
														(Art. 6(1)(b))
													</td>
												</tr>
												<tr>
													<td className="px-4 py-2 border border-white/10">
														Account creation,
														authentication, support
													</td>
													<td className="px-4 py-2 border border-white/10">
														Contractual necessity
													</td>
												</tr>
												<tr>
													<td className="px-4 py-2 border border-white/10">
														AI processing to
														generate outputs
													</td>
													<td className="px-4 py-2 border border-white/10">
														Contractual necessity
													</td>
												</tr>
												<tr>
													<td className="px-4 py-2 border border-white/10">
														Improve features,
														analytics
													</td>
													<td className="px-4 py-2 border border-white/10">
														Legitimate interests
														(Art. 6(1)(f))
													</td>
												</tr>
												<tr>
													<td className="px-4 py-2 border border-white/10">
														Marketing with consent
														(where required)
													</td>
													<td className="px-4 py-2 border border-white/10">
														Consent (Art. 6(1)(a))
													</td>
												</tr>
												<tr>
													<td className="px-4 py-2 border border-white/10">
														Legal compliance
													</td>
													<td className="px-4 py-2 border border-white/10">
														Legal obligation (Art.
														6(1)(c))
													</td>
												</tr>
											</tbody>
										</table>
									</div>
								</div>

								{/* Data Residency & Transfers */}
								<div>
									<h2 className="text-2xl font-bold mb-4">
										5. DATA RESIDENCY & TRANSFERS
									</h2>
									<p className="text-muted-foreground leading-relaxed mb-3">
										TabStudio follows an EU-first data
										residency principle, meaning:
									</p>
									<ul className="list-disc list-inside text-muted-foreground space-y-2 mb-3">
										<li>
											EU/UK user personal data is stored
											and processed within the EEA or UK
										</li>
										<li>
											Non-EU users may be stored in other
											regions as necessary for service
											delivery and redundancy
										</li>
										<li>
											Temporary compute processing (e.g.,
											GPU nodes) may occur outside the EU,
											but long-term storage will not,
											unless you consent or request it
										</li>
									</ul>
									<p className="text-muted-foreground leading-relaxed mb-3">
										For cross-border transfers, we rely on:
									</p>
									<ul className="list-disc list-inside text-muted-foreground space-y-2">
										<li>
											Standard Contractual Clauses (SCCs)
										</li>
										<li>
											Art. 46 GDPR transfer safeguards
										</li>
										<li>Encryption and access controls</li>
									</ul>
								</div>

								{/* AI Training Policy */}
								<div>
									<h2 className="text-2xl font-bold mb-4">
										6. AI TRAINING POLICY
									</h2>
									<p className="text-muted-foreground leading-relaxed mb-3">
										TabStudio follows a strict privacy-first
										AI training approach:
									</p>
									<ul className="list-disc list-inside text-muted-foreground space-y-2 mb-3">
										<li>
											We <strong>DO NOT</strong> use your
											personal data or uploaded content to
											train our AI models by default.
										</li>
										<li>
											If we ever use data for model
											improvement, it is only with your
											explicit opt-in consent.
										</li>
										<li>
											You may withdraw consent at any
											time.
										</li>
									</ul>
									<p className="text-muted-foreground leading-relaxed">
										This applies to all user-generated
										content, including photos, scripts,
										voice, and video.
									</p>
								</div>

								{/* User Rights */}
								<div>
									<h2 className="text-2xl font-bold mb-4">
										7. USER RIGHTS
									</h2>
									<p className="text-muted-foreground leading-relaxed mb-3">
										Depending on your jurisdiction, you have
										rights to:
									</p>
									<ul className="list-disc list-inside text-muted-foreground space-y-2 mb-3">
										<li>Access your data (Art. 15 GDPR)</li>
										<li>
											Rectify inaccurate data (Art. 16)
										</li>
										<li>
											Erase data (&quot;Right to be
											forgotten&quot;, Art. 17)
										</li>
										<li>Restrict processing (Art. 18)</li>
										<li>Port your data (Art. 20)</li>
										<li>Object to processing (Art. 21)</li>
										<li>
											Not be subject to automated
											decision-making (Art. 22)
										</li>
									</ul>
									<p className="text-muted-foreground leading-relaxed">
										Submit requests via{' '}
										<a
											href="mailto:privacy@tabspace.ai"
											className="text-primary hover:underline"
										>
											privacy@tabspace.ai
										</a>
										. We will respond within 30 days (or
										statutory period).
									</p>
								</div>

								{/* Data Retention */}
								<div>
									<h2 className="text-2xl font-bold mb-4">
										8. DATA RETENTION
									</h2>
									<p className="text-muted-foreground leading-relaxed mb-3">
										We retain data only as long as
										necessary:
									</p>
									<div className="overflow-x-auto mb-3">
										<table className="min-w-full text-sm border border-white/10">
											<thead className="bg-white/5">
												<tr>
													<th className="px-4 py-2 text-left border border-white/10">
														Data Type
													</th>
													<th className="px-4 py-2 text-left border border-white/10">
														Retention
													</th>
												</tr>
											</thead>
											<tbody>
												<tr>
													<td className="px-4 py-2 border border-white/10">
														Account data
													</td>
													<td className="px-4 py-2 border border-white/10">
														For the life of the
														account
													</td>
												</tr>
												<tr>
													<td className="px-4 py-2 border border-white/10">
														User content
													</td>
													<td className="px-4 py-2 border border-white/10">
														Until deleted by you
													</td>
												</tr>
												<tr>
													<td className="px-4 py-2 border border-white/10">
														Inference/cache data
													</td>
													<td className="px-4 py-2 border border-white/10">
														Short-lived;
														automatically purged
													</td>
												</tr>
												<tr>
													<td className="px-4 py-2 border border-white/10">
														Billing records
													</td>
													<td className="px-4 py-2 border border-white/10">
														7 years (compliance)
													</td>
												</tr>
												<tr>
													<td className="px-4 py-2 border border-white/10">
														Logs
													</td>
													<td className="px-4 py-2 border border-white/10">
														90–365 days
														(security/diagnostics)
													</td>
												</tr>
											</tbody>
										</table>
									</div>
									<p className="text-muted-foreground leading-relaxed">
										You may delete your account or content
										at any time.
									</p>
								</div>

								{/* Data Sharing */}
								<div>
									<h2 className="text-2xl font-bold mb-4">
										9. DATA SHARING & SUB-PROCESSORS
									</h2>
									<p className="text-muted-foreground leading-relaxed mb-3">
										We do not sell personal data.
									</p>
									<p className="text-muted-foreground leading-relaxed mb-3">
										We may share data with:
									</p>
									<ul className="list-disc list-inside text-muted-foreground space-y-2 mb-3">
										<li>
											Cloud infrastructure providers
											(e.g., AWS, Nebius)
										</li>
										<li>
											Payment processors (e.g., Stripe)
										</li>
										<li>Analytics providers</li>
										<li>Enterprise support vendors</li>
									</ul>
									<p className="text-muted-foreground leading-relaxed">
										All sub-processors are bound by DPA +
										SCCs.
									</p>
								</div>

								{/* Cookies */}
								<div>
									<h2 className="text-2xl font-bold mb-4">
										10. COOKIES
									</h2>
									<p className="text-muted-foreground leading-relaxed mb-3">
										We use:
									</p>
									<ul className="list-disc list-inside text-muted-foreground space-y-2 mb-3">
										<li>
											Strictly necessary cookies (login,
											session)
										</li>
										<li>
											Analytics cookies (only with consent
											— compliant with GDPR + ePrivacy)
										</li>
									</ul>
									<p className="text-muted-foreground leading-relaxed">
										You may manage cookies via our Consent
										Banner.
									</p>
								</div>

								{/* Security */}
								<div>
									<h2 className="text-2xl font-bold mb-4">
										11. SECURITY
									</h2>
									<p className="text-muted-foreground leading-relaxed mb-3">
										We implement industry-standard controls,
										including:
									</p>
									<ul className="list-disc list-inside text-muted-foreground space-y-2 mb-3">
										<li>
											Encryption at rest and in transit
											(TLS, AES-256)
										</li>
										<li>Role-based access (RBAC)</li>
										<li>Audit logging</li>
										<li>Secure development practices</li>
										<li>
											Incident response plan aligned to
											Art. 33 GDPR (72-hour breach rule)
										</li>
									</ul>
								</div>

								{/* Children's Data */}
								<div>
									<h2 className="text-2xl font-bold mb-4">
										12. CHILDREN&apos;S DATA
									</h2>
									<p className="text-muted-foreground leading-relaxed">
										The Service is not intended for children
										under 13, and under 16 for EU users. We
										do not knowingly collect children&apos;s
										data.
									</p>
								</div>

								{/* Policy Changes */}
								<div>
									<h2 className="text-2xl font-bold mb-4">
										13. POLICY CHANGES
									</h2>
									<p className="text-muted-foreground leading-relaxed">
										We may update this Policy periodically.
										We will provide notice of material
										changes.
									</p>
								</div>

								{/* Contact */}
								<div>
									<h2 className="text-2xl font-bold mb-4">
										14. CONTACT
									</h2>
									<p className="text-muted-foreground leading-relaxed mb-3">
										For questions or Data Subject Requests
										(DSR):
									</p>
									<p className="text-muted-foreground leading-relaxed">
										<strong>
											Data Protection Officer — TabStudio
										</strong>
										<br />
										Email:{' '}
										<a
											href="mailto:privacy@tabspace.ai"
											className="text-primary hover:underline"
										>
											privacy@tabspace.ai
										</a>
									</p>
									<p className="text-muted-foreground leading-relaxed text-sm mt-4 text-center font-semibold">
										END OF POLICY
									</p>
								</div>
							</div>
						</div>
					</div>
				</section>

				{/* CTA Section */}
				<section className="py-16 px-4">
					<div className="container mx-auto">
						<div className="glass-gradient glass-hover relative max-w-4xl mx-auto text-center p-8 rounded-3xl border border-white/20 overflow-hidden shadow-2xl">
							<div className="relative z-10">
								<h2 className="text-xl font-bold mb-6 text-center">
									Questions About Privacy?
								</h2>
								<p className="text-sm text-muted-foreground mb-6 max-w-2xl mx-auto">
									If you have any questions about this Privacy
									Policy or our data practices, please contact
									our Data Protection Office.
								</p>
								<a href="mailto:privacy@tabspace.ai">
									<Button
										variant="default"
										size="lg"
										className="glass-frosted glass-hover bg-white/90 text-black font-semibold border-white/30"
									>
										Contact Privacy Team
									</Button>
								</a>
							</div>
						</div>
					</div>
				</section>
			</main>

			{/* Footer */}
			<footer className="border-t border-border/50 bg-card/30 backdrop-blur-sm">
				<div className="container mx-auto px-4 py-12">
					<div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-8">
						<div>
							<h4 className="font-semibold mb-4">Product</h4>
							<ul className="space-y-2 text-sm text-muted-foreground">
								<li>
									<Link href="/features">Features</Link>
								</li>
							</ul>
						</div>
						<div>
							<h4 className="font-semibold mb-4">Company</h4>
							<ul className="space-y-2 text-sm text-muted-foreground">
								<li>
									<Link href="/about">About</Link>
								</li>
								<li>
									<Link href="/careers">Careers</Link>
								</li>
							</ul>
						</div>
						<div>
							<h4 className="font-semibold mb-4">Resources</h4>
							<ul className="space-y-2 text-sm text-muted-foreground">
								<li>
									<Link href="/help">Help Center</Link>
								</li>
								<li>
									<Link href="/api">API</Link>
								</li>
							</ul>
						</div>
						<div>
							<h4 className="font-semibold mb-4">Legal</h4>
							<ul className="space-y-2 text-sm text-muted-foreground">
								<li>
									<Link href="/privacy">Privacy</Link>
								</li>
								<li>
									<Link href="/terms">Terms</Link>
								</li>
								<li>
									<Link href="/security">Security</Link>
								</li>
							</ul>
						</div>
					</div>
					<div className="pt-8 border-t border-border/50 text-center text-sm text-muted-foreground">
						<p>
							&copy; 2025 TabStudio. Powered by AI. Built for
							creators.
						</p>
					</div>
				</div>
			</footer>
		</div>
	);
}
